import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影分类", to: "/category" },
  { label: "剧情解析", to: "/features/plot-analysis" },
  { label: "观影指南", to: "/guides" },
  { label: "演员介绍", to: "/actors/li-lizhen" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !isHome || scrolled || menuOpen;

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
    setQuery("");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span
              className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                solid ? "bg-primary-500 text-background-50" : "bg-background-50/90 text-primary-600"
              }`}
            >
              <i className="ri-movie-2-line text-lg"></i>
            </span>
            <span
              className={`font-heading text-lg md:text-xl font-semibold tracking-wide ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              蜜桃成熟时1997
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => {
              const active = item.to === "/" ? isHome : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                    solid
                      ? active
                        ? "text-primary-600 bg-primary-50"
                        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      : active
                        ? "text-background-50 bg-white/20"
                        : "text-background-50/90 hover:text-background-50 hover:bg-white/15"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center">
              <div
                className={`flex items-center gap-2 rounded-full pl-3 pr-1 py-1 border transition-colors ${
                  solid ? "bg-background-100 border-background-200" : "bg-white/20 border-white/30"
                }`}
              >
                <span
                  className={`w-5 h-5 flex items-center justify-center ${
                    solid ? "text-foreground-500" : "text-background-50"
                  }`}
                >
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="搜索影片、演员、资讯"
                  className={`bg-transparent outline-none text-sm w-40 xl:w-52 ${
                    solid
                      ? "text-foreground-900 placeholder:text-foreground-400"
                      : "text-background-50 placeholder:text-background-50/70"
                  }`}
                />
                <button
                  type="submit"
                  className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer shrink-0"
                  aria-label="搜索"
                >
                  <i className="ri-arrow-right-line text-base"></i>
                </button>
              </div>
            </form>

            <Link
              to="/search"
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-full ${
                solid ? "text-foreground-800 hover:bg-background-100" : "text-background-50"
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl"></i>
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full ${
                solid ? "text-foreground-900 hover:bg-background-100" : "text-background-50"
              }`}
              aria-label="菜单"
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-2">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="flex items-center gap-2 rounded-full px-3 py-2 bg-background-100 border border-background-200">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索影片、演员、资讯"
                className="bg-transparent outline-none text-sm flex-1 text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="px-3 py-3 rounded-md text-sm text-foreground-800 hover:bg-background-100 border-b border-background-100 last:border-0"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}